import { type FC, useEffect } from 'react'
import { CircularProgress } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { PageWithRefresh } from '@dhl-ui/page'
import { DocumentTitle } from '@dhl/receiver-a11y'
import { useIsMobileVariant, Box, Button } from '@dhl-ui/components'
import { ChevronLeftIcon } from '@dhl-ui/icons'
import { type PageProps, type PageResponsiveProps } from './types.js'

// FIXME: loading pages should mostly be skeletons?
const Loading = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-end',
      height: '40vh',
    }}
  >
    <CircularProgress size={40} />
  </Box>
)

export const Page: FC<
  PageProps &
    Pick<
      // We can also render a full page from a sub page reference
      PageResponsiveProps,
      /**
       * Only use when your page is not a main page but a subpage of a main part
       */
      'onBack' | 'isMaximizedCentered' | 'hasMaxSize'
    >
> = ({
  children,
  'data-testid': testid,
  onRefresh,
  disablePullDown,
  title,
  isLoading,
  isMaximizedCentered,
  isFullView,
  hasMaxSize,
  sideMenu,
  bottomMenu,
  header,
  endNode,
  onBack,
  ...props
}) => {
  const isMobileVariant = useIsMobileVariant()
  const { t } = useTranslation()

  useEffect(() => {
    document.getElementById('root')?.scrollTo(0, 0)
  }, [])

  const content = (
    <PageWithRefresh.Content
      // FIXME: data-testid was not official prop of component, lets see later
      data-testid={testid}
      isFullView={isFullView}
      isMaximizedCentered={isMaximizedCentered}
      hasMaxSize={hasMaxSize}
      hasBottomMenu={isMobileVariant}
      sx={{
        overflowX: 'hidden',
      }}
    >
      {isLoading ? (
        <Loading />
      ) : (
        <>
          {onBack && (
            <Button
              dense
              onClick={onBack}
              data-testid="sub-page-toolbar-back"
              sx={{ mb: 2, alignSelf: 'flex-start' }}
              startIcon={<ChevronLeftIcon />}
            >
              {t('receiver-page.back')}
            </Button>
          )}

          <Box mt={isMobileVariant ? 0 : 1.5}>{children}</Box>
        </>
      )}
    </PageWithRefresh.Content>
  )

  return (
    <>
      <DocumentTitle title={title} />

      <PageWithRefresh
        sideMenu={sideMenu}
        header={header}
        bottomMenu={bottomMenu}
        {...props}
        onRefresh={onRefresh}
        disablePullDown={!onRefresh}
      >
        {content}
      </PageWithRefresh>
      {endNode}
    </>
  )
}
